import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import AdminMessageAttachments from '../components/admin/AdminMessageAttachments';
import PaystackSubaccountSetup from '../components/admin/PaystackSubaccountSetup';
import {
  approveAdminDriverChangeRequest,
  approveAdminDriver,
  fetchAdminConversationMessages,
  fetchAdminCompanyPayoutReviewDetail,
  fetchAdminDriverEarnings,
  fetchAdminDriverReview,
  isSuperAdminRole,
  fetchAdminReviewConversation,
  rejectAdminDriverChangeRequest,
  rejectAdminDriver,
  rerunAdminCompanyMatching,
  rerunAdminDriverCompanyMatching,
  sendAdminReviewMessage,
  updateAdminCompanyPayoutPaystackSubaccount,
  updateAdminDriverGitReview,
  updateAdminDriverEarlyAdopterStatus,
  updateAdminDriverEnabledStatus,
  updateAdminDriverLockStatus,
  updateAdminDriverPaystackSubaccount,
} from '../services/api/admin';
import type {
  AdminCompanyMatchingRefreshResponse,
  AdminCompanyPayoutReview,
  AdminDriverEarningRecord,
  AdminDriverGitReviewPayload,
  AdminDriverReviewDetail,
  AdminReviewConversation,
  AdminReviewMessage,
  DriverGitStatus,
  PaystackSubaccountUpdatePayload,
} from '../types/admin';
import { getAdminAuthUser } from '../services/storage/adminFlow';
import { formatCurrency, formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

function formatReviewDetails(value: string | null | undefined): string {
  if (!value) return 'No additional details recorded.';

  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    return Object.entries(parsed)
      .map(([key, entry]) => `${key}: ${String(entry)}`)
      .join('\n');
  } catch {
    return value;
  }
}

function hasValidSplitPercentage(value: number | null | undefined): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 100;
}

function readinessPillClass(ready: boolean): string {
  return ready ? 'admin-pill admin-pill-success' : 'admin-pill admin-pill-warning';
}

function gitStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'VERIFIED':
      return 'GIT verified';
    case 'PENDING_REVIEW':
      return 'GIT pending review';
    case 'REJECTED':
      return 'GIT rejected';
    case 'NOT_COVERED':
      return 'No GIT cover';
    case 'NOT_DECLARED':
      return 'GIT not declared';
    default:
      return 'GIT unknown';
  }
}

function gitStatusPillClass(status: string | null | undefined): string {
  if (status === 'VERIFIED') return 'admin-pill admin-pill-success';
  if (status === 'PENDING_REVIEW') return 'admin-pill admin-pill-warning';
  return 'admin-pill';
}

const AdminDriverReviewPage: React.FC = () => {
  const { driverId = '' } = useParams<{ driverId: string }>();
  const adminUser = getAdminAuthUser();
  const isSuperAdmin = isSuperAdminRole(adminUser?.roles);
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [messageLoading, setMessageLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<
    'approve' | 'reject' | 'lock' | 'enabled' | 'early-adopter' | 'git-review' | 'rerun-driver-match' | 'rerun-company-match' | null
  >(null);
  const [error, setError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [detail, setDetail] = useState<AdminDriverReviewDetail | null>(null);
  const [earnings, setEarnings] = useState<AdminDriverEarningRecord[]>([]);
  const [earningsError, setEarningsError] = useState('');
  const [companyPayoutReview, setCompanyPayoutReview] = useState<AdminCompanyPayoutReview | null>(null);
  const [companyPayoutLoading, setCompanyPayoutLoading] = useState(false);
  const [companyPayoutError, setCompanyPayoutError] = useState('');
  const [conversation, setConversation] = useState<AdminReviewConversation | null>(null);
  const [messages, setMessages] = useState<AdminReviewMessage[]>([]);
  const [conversationError, setConversationError] = useState('');
  const [messageDraft, setMessageDraft] = useState('');
  const [messageError, setMessageError] = useState('');
  const [messageSuccess, setMessageSuccess] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [dangerousGoodsAccredited, setDangerousGoodsAccredited] = useState<boolean | null>(null);
  const [gitStatus, setGitStatus] = useState<DriverGitStatus>('PENDING_REVIEW');
  const [gitCoverAmount, setGitCoverAmount] = useState('');
  const [gitPolicyExpiry, setGitPolicyExpiry] = useState('');
  const [gitReviewNotes, setGitReviewNotes] = useState('');
  const [messageSentSeparately, setMessageSentSeparately] = useState(false);
  const [changeRequestLoadingId, setChangeRequestLoadingId] = useState<string | null>(null);
  const [changeRequestRejectReasons, setChangeRequestRejectReasons] = useState<Record<string, string>>({});
  const [isMessageListNearBottom, setIsMessageListNearBottom] = useState(true);
  const [pendingDriverReplyId, setPendingDriverReplyId] = useState<string | null>(null);
  const [acknowledgedLatestMessageId, setAcknowledgedLatestMessageId] = useState<string | null>(null);

  const attachmentEntries = useMemo(
    () => Object.entries(detail?.driver.attachments ?? {}),
    [detail?.driver.attachments],
  );
  const gitProof = detail?.driver.attachments?.INSURANCE_LETTER ?? null;
  const gitStatusClass = gitStatusPillClass(detail?.driver.gitStatus);
  const riskModeClass = detail?.driver.riskMode === 'FULL_ACCESS' ? 'admin-pill admin-pill-success' : 'admin-pill admin-pill-warning';
  const conversationThreadKey = conversation?.bookingId || conversation?.conversationId || null;
  const latestMessage = messages.at(-1) ?? null;
  const normalizedCompanyId = detail?.driver.companyDetails?.normalizedCompanyId?.trim() || '';
  const isApproved =
    detail?.onboardingReviewStatus?.toUpperCase() === 'APPROVED' ||
    detail?.driver.onboardingsStep?.toUpperCase() === 'COMPLETED';
  const isRejected = detail?.onboardingReviewStatus?.toUpperCase() === 'REJECTED';
  const isLocked = detail?.driver.accountNonLocked === false;
  const isEnabled = detail?.driver.enabled !== false;
  const isEarlyAdopter = detail?.driver.earlyAdopter === true;
  const dangerousGoodsReviewed = Boolean(detail?.driver.dangerousGoodsReviewedAt);
  const bankVerificationStatus = detail?.driver.bankDetails?.verificationStatus || 'Not supplied';
  const bankVerifiedAt = detail?.driver.bankDetails?.verifiedAt || null;
  const isIndividualOwner = detail?.driver.accountType?.toUpperCase() === 'INDIVIDUAL_OWNER';
  const isCompanyDriver = detail?.driver.accountType?.toUpperCase() === 'COMPANY_DRIVER';
  const bankAccountHolder =
    detail?.driver.bankDetails?.accountHolderName ||
    detail?.driver.bankDetails?.accountHolder ||
    '';
  const bankBranchCode = detail?.driver.bankDetails?.branchCode?.trim() || '';
  const individualPaystackSource = detail?.driver.bankDetails ?? null;
  const companyPaystackSource = companyPayoutReview ?? null;
  const activePaystackSource = isIndividualOwner ? individualPaystackSource : companyPaystackSource;
  const hasBankOrPayoutDetails = isIndividualOwner
    ? Boolean(individualPaystackSource)
    : Boolean(normalizedCompanyId && companyPaystackSource?.payoutDetailsPresent);
  const hasPaystackSubaccountCode = Boolean(activePaystackSource?.paystackSubaccountCode?.trim());
  const hasVerifiedPaystackStatus = activePaystackSource?.paystackSubaccountStatus === 'VERIFIED';
  const hasSplitPercentage = hasValidSplitPercentage(activePaystackSource?.paystackSplitPercentage);
  const companyPayoutActive =
    !isCompanyDriver || (companyPaystackSource?.payoutStatus ? companyPaystackSource.payoutStatus === 'ACTIVE' : true);
  const paystackApprovalReady = isIndividualOwner
    ? hasBankOrPayoutDetails && hasPaystackSubaccountCode && hasVerifiedPaystackStatus && hasSplitPercentage
    : isCompanyDriver
      ? Boolean(normalizedCompanyId && companyPaystackSource)
        && hasBankOrPayoutDetails
        && companyPayoutActive
        && hasPaystackSubaccountCode
        && hasVerifiedPaystackStatus
        && hasSplitPercentage
      : true;
  const paystackReadinessItems = [
    { label: isIndividualOwner ? 'Bank details present' : 'Company payout details present', ready: hasBankOrPayoutDetails },
    { label: 'Paystack subaccount code captured', ready: hasPaystackSubaccountCode },
    { label: 'Paystack status verified', ready: hasVerifiedPaystackStatus },
    { label: 'Split percentage configured', ready: hasSplitPercentage },
    ...(isCompanyDriver ? [{ label: 'Company payout active', ready: companyPayoutActive }] : []),
  ];

  const syncMessageListPosition = (behavior: ScrollBehavior = 'smooth') => {
    const element = messageListRef.current;
    if (!element) return;

    element.scrollTo({
      top: element.scrollHeight,
      behavior,
    });
  };

  const handleMessageListScroll = () => {
    const element = messageListRef.current;
    if (!element) return;

    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    const nearBottom = distanceFromBottom <= 40;
    setIsMessageListNearBottom(nearBottom);

    if (nearBottom) {
      if (latestMessage?.id) {
        setAcknowledgedLatestMessageId(latestMessage.id);
      }
      setPendingDriverReplyId(null);
    }
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!driverId) {
        setError('Driver ID is missing.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      setActionSuccess('');
      setEarningsError('');
      setConversationError('');

      try {
        const reviewDetail = await fetchAdminDriverReview(driverId);

        if (!mounted) return;
        setDetail(reviewDetail);
      } catch (loadError: any) {
        if (!mounted) return;
        setError(loadError.message || 'Could not load the admin review detail.');
        setDetail(null);
        setEarnings([]);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }

      if (!isSuperAdmin) {
        setEarnings([]);
        setEarningsError('');
      } else {
        try {
          const earningsRows = await fetchAdminDriverEarnings(driverId);
          if (!mounted) return;
          setEarnings(earningsRows);
          setEarningsError('');
        } catch (earningsLoadError: any) {
          if (!mounted) return;
          setEarnings([]);
          const resolvedMessage =
            earningsLoadError.message === 'This page is only available to admin accounts.'
              ? 'Driver earnings are not available for this admin account in the current backend environment.'
              : earningsLoadError.message || 'Driver earnings are temporarily unavailable.';
          setEarningsError(
            resolvedMessage,
          );
        }
      }

      try {
        const reviewConversation = await fetchAdminReviewConversation(driverId);
        const reviewThreadKey = reviewConversation.bookingId || reviewConversation.conversationId;
        const reviewMessages = await fetchAdminConversationMessages(reviewThreadKey);

        if (!mounted) return;
        setConversation(reviewConversation);
        setMessages(reviewMessages);
        setAcknowledgedLatestMessageId(reviewMessages.at(-1)?.id ?? null);
        setPendingDriverReplyId(null);
      } catch (conversationLoadError: any) {
        if (!mounted) return;
        setConversation(null);
        setMessages([]);
        setAcknowledgedLatestMessageId(null);
        setPendingDriverReplyId(null);
        setConversationError(
          conversationLoadError.message || 'The review conversation is temporarily unavailable.',
        );
      }
    };

    void load();

    return () => {
      mounted = false;
    };
  }, [driverId]);

  useEffect(() => {
    let mounted = true;

    const loadCompanyPayoutReview = async () => {
      if (!isCompanyDriver || !normalizedCompanyId) {
        setCompanyPayoutReview(null);
        setCompanyPayoutError('');
        return;
      }

      setCompanyPayoutLoading(true);
      setCompanyPayoutError('');

      try {
        const payoutDetail = await fetchAdminCompanyPayoutReviewDetail(normalizedCompanyId);
        if (!mounted) return;
        setCompanyPayoutReview(payoutDetail);
      } catch (loadError: any) {
        if (!mounted) return;
        setCompanyPayoutReview(null);
        setCompanyPayoutError(loadError.message || 'Could not load company payout Paystack setup.');
      } finally {
        if (mounted) {
          setCompanyPayoutLoading(false);
        }
      }
    };

    void loadCompanyPayoutReview();

    return () => {
      mounted = false;
    };
  }, [isCompanyDriver, normalizedCompanyId]);

  useEffect(() => {
    if (!conversationThreadKey) return undefined;

    const interval = window.setInterval(async () => {
      try {
        const latestMessages = await fetchAdminConversationMessages(conversationThreadKey);
        setMessages(latestMessages);
        setConversationError('');
      } catch {
        setConversationError('Live message refresh is temporarily unavailable.');
      }
    }, 15000);

    return () => window.clearInterval(interval);
  }, [conversationThreadKey]);

  useEffect(() => {
    if (!messages.length) return;

    const newestMessage = messages[messages.length - 1];
    const senderType = newestMessage.senderType?.toUpperCase();
    const fromDriver = senderType === 'DRIVER';

    if (isMessageListNearBottom) {
      window.requestAnimationFrame(() => syncMessageListPosition(messages.length === 1 ? 'auto' : 'smooth'));
      if (newestMessage.id !== acknowledgedLatestMessageId) {
        setAcknowledgedLatestMessageId(newestMessage.id);
      }
      if (pendingDriverReplyId) setPendingDriverReplyId(null);
      return;
    }

    if (fromDriver && newestMessage.id !== acknowledgedLatestMessageId) {
      setPendingDriverReplyId((current) => (current === newestMessage.id ? current : newestMessage.id));
    }
  }, [messages, isMessageListNearBottom, pendingDriverReplyId, acknowledgedLatestMessageId]);

  useEffect(() => {
    if (!detail) {
      setDangerousGoodsAccredited(null);
      return;
    }

    if (dangerousGoodsReviewed || isApproved) {
      setDangerousGoodsAccredited(Boolean(detail.driver.dangerousGoodsAccredited));
      return;
    }

    setDangerousGoodsAccredited(null);
  }, [detail?.driver.id, detail?.driver.dangerousGoodsAccredited, detail?.driver.dangerousGoodsReviewedAt, dangerousGoodsReviewed, isApproved]);

  useEffect(() => {
    if (!detail) {
      setGitStatus('PENDING_REVIEW');
      setGitCoverAmount('');
      setGitPolicyExpiry('');
      setGitReviewNotes('');
      return;
    }

    setGitStatus((detail.driver.gitStatus as DriverGitStatus | null) || 'PENDING_REVIEW');
    setGitCoverAmount(
      detail.driver.gitCoverAmount !== null && detail.driver.gitCoverAmount !== undefined
        ? String(detail.driver.gitCoverAmount)
        : '',
    );
    setGitPolicyExpiry(detail.driver.gitPolicyExpiry || '');
    setGitReviewNotes(detail.driver.gitReviewNotes || '');
  }, [detail?.driver.id, detail?.driver.gitStatus, detail?.driver.gitCoverAmount, detail?.driver.gitPolicyExpiry, detail?.driver.gitReviewNotes]);

  const applyUpdatedDriver = (updatedDriver: AdminDriverReviewDetail['driver']) => {
    setDetail((current) => (current ? { ...current, driver: { ...current.driver, ...updatedDriver } } : current));
  };

  const refreshDriverReview = async () => {
    if (!driverId) return;
    const refreshedDetail = await fetchAdminDriverReview(driverId);
    setDetail(refreshedDetail);
  };

  const summarizeMatchingRun = (response: AdminCompanyMatchingRefreshResponse): string => {
    const affectedCount = response.affectedCompanyUuids.length;
    return [
      `${response.scope || 'MATCH'} rerun complete.`,
      `${response.linkedCount} linked,`,
      `${response.pendingReviewCount} pending review,`,
      `${response.manualReviewCount} manual review,`,
      affectedCount ? `${affectedCount} affected compan${affectedCount === 1 ? 'y' : 'ies'}.` : 'no affected companies.',
    ].join(' ');
  };

  const handleApprove = async () => {
    if (!driverId) return;
    if (dangerousGoodsAccredited === null) {
      setError('Confirm whether the driver has dangerous goods accreditation before approving onboarding.');
      return;
    }

    setActionLoading('approve');
    setError('');
    setActionSuccess('');

    try {
      const nextDetail = await approveAdminDriver(driverId, { dangerousGoodsAccredited });
      setDetail(nextDetail);
      setActionSuccess('Driver onboarding approved.');
    } catch (actionError: any) {
      setError(actionError.message || 'Could not approve this driver.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!driverId) return;
    if (!rejectReason.trim()) {
      setError('Add a rejection reason before rejecting the onboarding.');
      return;
    }

    setActionLoading('reject');
    setError('');
    setActionSuccess('');

    try {
      const nextDetail = await rejectAdminDriver({
        driverId,
        reason: rejectReason.trim(),
        messageSentSeparately,
      });
      setDetail(nextDetail);
      setActionSuccess('Driver onboarding rejected.');
    } catch (actionError: any) {
      setError(actionError.message || 'Could not reject this driver.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveChangeRequest = async (changeRequestId: string) => {
    setChangeRequestLoadingId(changeRequestId);
    setError('');
    setActionSuccess('');

    try {
      await approveAdminDriverChangeRequest({
        changeRequestId,
        reviewedBy: adminUser?.email ?? null,
      });
      await refreshDriverReview();
      setActionSuccess('Driver profile change approved.');
      setChangeRequestRejectReasons((current) => {
        const next = { ...current };
        delete next[changeRequestId];
        return next;
      });
    } catch (actionError: any) {
      setError(actionError.message || 'Could not approve this profile change.');
    } finally {
      setChangeRequestLoadingId(null);
    }
  };

  const handleRejectChangeRequest = async (changeRequestId: string) => {
    const reason = changeRequestRejectReasons[changeRequestId]?.trim() || '';
    if (!reason) {
      setError('Add a rejection reason before rejecting the profile change.');
      return;
    }

    setChangeRequestLoadingId(changeRequestId);
    setError('');
    setActionSuccess('');

    try {
      await rejectAdminDriverChangeRequest({
        changeRequestId,
        reason,
        reviewedBy: adminUser?.email ?? null,
      });
      await refreshDriverReview();
      setActionSuccess('Driver profile change rejected.');
      setChangeRequestRejectReasons((current) => {
        const next = { ...current };
        delete next[changeRequestId];
        return next;
      });
    } catch (actionError: any) {
      setError(actionError.message || 'Could not reject this profile change.');
    } finally {
      setChangeRequestLoadingId(null);
    }
  };

  const handleSaveGitReview = async () => {
    if (!driverId) return;

    const coverAmount = gitCoverAmount.trim() ? Number(gitCoverAmount) : null;
    const payload: AdminDriverGitReviewPayload = {
      gitStatus,
      gitCoverAmount: coverAmount,
      gitPolicyExpiry: gitPolicyExpiry || null,
      gitReviewNotes: gitReviewNotes.trim() || null,
    };

    if (gitStatus === 'VERIFIED') {
      if (!coverAmount || !Number.isFinite(coverAmount) || coverAmount <= 0) {
        setError('Enter a positive GIT cover amount before marking the proof verified.');
        return;
      }
      if (!gitPolicyExpiry) {
        setError('Enter the GIT policy expiry date before marking the proof verified.');
        return;
      }
    }

    setActionLoading('git-review');
    setError('');
    setActionSuccess('');

    try {
      const updatedDriver = await updateAdminDriverGitReview(driverId, payload);
      applyUpdatedDriver(updatedDriver);
      setActionSuccess('Driver GIT review updated.');
    } catch (actionError: any) {
      setError(actionError.message || 'Could not update the driver GIT review.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLockToggle = async () => {
    if (!driverId) return;

    setActionLoading('lock');
    setError('');
    setActionSuccess('');

    try {
      const updatedDriver = await updateAdminDriverLockStatus(driverId, !isLocked);
      applyUpdatedDriver(updatedDriver);
      setActionSuccess(updatedDriver.accountNonLocked === false ? 'Driver account locked.' : 'Driver account unlocked.');
    } catch (actionError: any) {
      setError(actionError.message || 'Could not update the driver lock status.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnabledToggle = async () => {
    if (!driverId) return;

    setActionLoading('enabled');
    setError('');
    setActionSuccess('');

    try {
      const updatedDriver = await updateAdminDriverEnabledStatus(driverId, !isEnabled);
      applyUpdatedDriver(updatedDriver);
      setActionSuccess(updatedDriver.enabled === false ? 'Driver account disabled.' : 'Driver account enabled.');
    } catch (actionError: any) {
      setError(actionError.message || 'Could not update the driver enabled status.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEarlyAdopterToggle = async () => {
    if (!driverId) return;

    setActionLoading('early-adopter');
    setError('');
    setActionSuccess('');

    try {
      const updatedDriver = await updateAdminDriverEarlyAdopterStatus(driverId, !isEarlyAdopter);
      applyUpdatedDriver(updatedDriver);
      setActionSuccess(updatedDriver.earlyAdopter ? 'Early adopter enabled.' : 'Early adopter disabled.');
    } catch (actionError: any) {
      setError(actionError.message || 'Could not update the early-adopter status.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDriverMatchingRerun = async () => {
    if (!driverId) return;

    setActionLoading('rerun-driver-match');
    setError('');
    setActionSuccess('');

    try {
      const response = await rerunAdminDriverCompanyMatching(driverId);
      await refreshDriverReview();
      setActionSuccess(summarizeMatchingRun(response));
    } catch (actionError: any) {
      setError(actionError.message || 'Could not rerun company matching for this driver.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCompanyMatchingRerun = async () => {
    if (!normalizedCompanyId) return;

    setActionLoading('rerun-company-match');
    setError('');
    setActionSuccess('');

    try {
      const response = await rerunAdminCompanyMatching(normalizedCompanyId);
      await refreshDriverReview();
      setActionSuccess(summarizeMatchingRun(response));
    } catch (actionError: any) {
      setError(actionError.message || 'Could not rerun company matching for this company.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSavePaystackSetup = async (payload: PaystackSubaccountUpdatePayload) => {
    if (!detail || !driverId) return;

    setError('');
    setActionSuccess('');

    if (isIndividualOwner) {
      const updatedDriver = await updateAdminDriverPaystackSubaccount(driverId, payload);
      applyUpdatedDriver(updatedDriver);
      setActionSuccess('Driver Paystack subaccount setup saved.');
      return;
    }

    if (!normalizedCompanyId) {
      throw new Error('A linked normalized company ID is required before saving company Paystack setup.');
    }

    const updatedPayoutReview = await updateAdminCompanyPayoutPaystackSubaccount(normalizedCompanyId, payload);
    setCompanyPayoutReview(updatedPayoutReview);
    setActionSuccess('Company Paystack subaccount setup saved.');
  };

  const handleSendMessage = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!driverId || !messageDraft.trim()) {
      setMessageError('Write a message before sending.');
      return;
    }

    setMessageLoading(true);
    setMessageError('');
    setMessageSuccess('');

    try {
      if (!conversation && conversationError) {
        throw new Error('Messaging is temporarily unavailable until the backend conversation fix is deployed.');
      }

      const sentMessage = await sendAdminReviewMessage({
        driverId,
        content: messageDraft.trim(),
      });
      setMessages((current) => [...current, sentMessage]);
      setMessageDraft('');
      setMessageSuccess('Message sent to the driver inbox.');
      setMessageSentSeparately(true);
      setPendingDriverReplyId(null);
      setAcknowledgedLatestMessageId(sentMessage.id);

      if (conversationThreadKey) {
        const latestMessages = await fetchAdminConversationMessages(conversationThreadKey);
        setMessages(latestMessages);
      }
    } catch (sendError: any) {
      setMessageError(sendError.message || 'Could not send the review message.');
    } finally {
      setMessageLoading(false);
    }
  };

  return (
    <AdminShell
      badge="Driver Review Detail"
      copy="Inspect the submitted onboarding, message the driver for corrections, and approve or reject the review outcome."
      title={detail?.driver.name ? `Review ${detail.driver.name}` : 'Driver review'}
    >
      {error ? <div className="register-inline-error">{error}</div> : null}
      {actionSuccess ? <div className="register-inline-success">{actionSuccess}</div> : null}

      {loading ? (
        <div className="admin-panel">
          <div className="register-loader" />
        </div>
      ) : detail ? (
        <>
          <div className="admin-panel" style={{ marginBottom: '18px' }}>
            <div className="admin-panel-header">
              <div>
                <h2 className="admin-panel-title">Review state</h2>
                <p className="admin-panel-copy">
                  Use the detail below to confirm the onboarding or send corrective feedback.
                </p>
              </div>
              <div className="admin-inline-actions">
                {!isApproved ? (
                  <button
                    className="register-submit register-submit-inline"
                    disabled={Boolean(actionLoading) || !paystackApprovalReady || dangerousGoodsAccredited === null}
                    onClick={handleApprove}
                    type="button"
                  >
                    {actionLoading === 'approve' ? 'Approving...' : 'Approve onboarding'}
                  </button>
                ) : (
                  <span className="admin-muted-note">
                    Approved successfully.
                  </span>
                )}
                <Link className="register-secondary-button" to="/admin/review-queue">
                  Back to queue
                </Link>
              </div>
            </div>
            <div className="admin-pill-row">
              <span className="admin-pill">{detail.onboardingReviewStatus || 'PENDING'}</span>
              <span className="admin-pill">{detail.driver.onboardingsStep || 'SUBMITTED'}</span>
              <span className="admin-pill">{detail.driver.accountType || 'Account type pending'}</span>
              <span className="admin-pill">{detail.driver.enabled ? 'Enabled' : 'Disabled'}</span>
              <span className="admin-pill">{detail.driver.earlyAdopter ? 'Early adopter' : 'Standard payout'}</span>
              <span className={gitStatusClass}>{gitStatusLabel(detail.driver.gitStatus)}</span>
              <span className={riskModeClass}>
                {detail.driver.riskMode === 'FULL_ACCESS' ? 'Full goods access' : 'Restricted no GIT'}
              </span>
              <span className={detail.driver.dangerousGoodsAccredited ? 'admin-pill admin-pill-success' : 'admin-pill'}>
                {detail.driver.dangerousGoodsAccredited ? 'Dangerous goods accredited' : 'No dangerous goods accreditation'}
              </span>
              <span className="admin-pill">{bankVerificationStatus}</span>
            </div>
            {isIndividualOwner ? (
              <p className="admin-muted-note" style={{ marginTop: '12px' }}>
                For individual owners, approving onboarding is the current backend review action that verifies the reviewed bank details and bank letter.
              </p>
            ) : null}
            <div className="admin-detail-grid" style={{ marginTop: '14px' }}>
              <div className="admin-detail-card admin-detail-card-span-2">
                <span>Paystack approval readiness</span>
                <strong>{paystackApprovalReady ? 'Ready for backend approval check' : 'Setup incomplete'}</strong>
                <div className="admin-pill-row" style={{ marginTop: '10px' }}>
                  {paystackReadinessItems.map((item) => (
                    <span className={readinessPillClass(item.ready)} key={item.label}>
                      {item.ready ? 'Done' : 'Needed'}: {item.label}
                    </span>
                  ))}
                </div>
                {!paystackApprovalReady ? (
                  <small className="admin-muted-note">
                    Complete and save the Paystack subaccount setup before approving this driver.
                  </small>
                ) : (
                  <small className="admin-muted-note">
                    Backend remains the source of truth and may still reject approval if server-side checks fail.
                  </small>
                )}
              </div>
              <div className="admin-detail-card admin-detail-card-span-2">
                <span>Dangerous goods accreditation</span>
                <strong>
                  {dangerousGoodsAccredited === null
                    ? 'Decision required'
                    : dangerousGoodsAccredited
                      ? 'Driver is accredited'
                      : 'Driver is not accredited'}
                </strong>
                <small className="admin-muted-note">
                  Review the PDP and supporting documents before approving onboarding.
                </small>
                <div className="admin-pill-row" style={{ marginTop: '10px' }}>
                  <button
                    aria-pressed={dangerousGoodsAccredited === true}
                    className={`admin-pill admin-pill-button${dangerousGoodsAccredited === true ? ' admin-pill-success admin-pill-button-active' : ''}`}
                    disabled={Boolean(actionLoading) || isApproved}
                    onClick={() => {
                      setDangerousGoodsAccredited(true);
                      setError('');
                    }}
                    type="button"
                  >
                    Accredited
                  </button>
                  <button
                    aria-pressed={dangerousGoodsAccredited === false}
                    className={`admin-pill admin-pill-button${dangerousGoodsAccredited === false ? ' admin-pill-warning admin-pill-button-active' : ''}`}
                    disabled={Boolean(actionLoading) || isApproved}
                    onClick={() => {
                      setDangerousGoodsAccredited(false);
                      setError('');
                    }}
                    type="button"
                  >
                    Not accredited
                  </button>
                </div>
                {detail.driver.dangerousGoodsReviewedAt ? (
                  <small className="admin-muted-note">
                    Last reviewed {formatDateTime(detail.driver.dangerousGoodsReviewedAt)}
                    {detail.driver.dangerousGoodsReviewedBy ? ` by ${detail.driver.dangerousGoodsReviewedBy}` : ''}.
                  </small>
                ) : null}
              </div>
              <div className="admin-detail-card admin-detail-card-span-2">
                <span>Goods in Transit cover</span>
                <strong>{gitStatusLabel(detail.driver.gitStatus)}</strong>
                <small className="admin-muted-note">
                  {detail.driver.riskMode === 'FULL_ACCESS'
                    ? 'Verified cover allows the driver to receive GIT-required and higher-risk goods.'
                    : 'Restricted drivers can still operate on low-risk MVP loads where the customer accepts no-GIT risk.'}
                </small>
                <div className="admin-pill-row" style={{ marginTop: '10px' }}>
                  {(['VERIFIED', 'PENDING_REVIEW', 'REJECTED', 'NOT_COVERED'] as DriverGitStatus[]).map((status) => (
                    <button
                      aria-pressed={gitStatus === status}
                      className={`admin-pill admin-pill-button${gitStatus === status ? ' admin-pill-button-active' : ''}${status === 'VERIFIED' && gitStatus === status ? ' admin-pill-success' : ''}${status === 'PENDING_REVIEW' && gitStatus === status ? ' admin-pill-warning' : ''}`}
                      disabled={Boolean(actionLoading)}
                      key={status}
                      onClick={() => {
                        setGitStatus(status);
                        setError('');
                      }}
                      type="button"
                    >
                      {gitStatusLabel(status)}
                    </button>
                  ))}
                </div>
                <div className="admin-detail-grid" style={{ marginTop: '12px' }}>
                  <label className="register-field">
                    <span>Cover amount</span>
                    <input
                      className="register-input"
                      min="0"
                      onChange={(event) => setGitCoverAmount(event.target.value)}
                      placeholder="Example: 50000"
                      type="number"
                      value={gitCoverAmount}
                    />
                  </label>
                  <label className="register-field">
                    <span>Policy expiry</span>
                    <input
                      className="register-input"
                      onChange={(event) => setGitPolicyExpiry(event.target.value)}
                      type="date"
                      value={gitPolicyExpiry}
                    />
                  </label>
                </div>
                <label className="register-field" style={{ marginTop: '12px' }}>
                  <span>Review notes</span>
                  <textarea
                    className="register-input"
                    onChange={(event) => setGitReviewNotes(event.target.value)}
                    placeholder="Record policy number, insurer, exclusions, or rejection reason."
                    rows={3}
                    value={gitReviewNotes}
                  />
                </label>
                <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
                  <button
                    className="register-submit register-submit-inline"
                    disabled={Boolean(actionLoading)}
                    onClick={handleSaveGitReview}
                    type="button"
                  >
                    {actionLoading === 'git-review' ? 'Saving...' : 'Save GIT review'}
                  </button>
                  {gitProof?.url ? (
                    <a className="register-secondary-button" href={gitProof.url} rel="noreferrer" target="_blank">
                      Open uploaded proof
                    </a>
                  ) : (
                    <span className="admin-muted-note">No GIT proof uploaded.</span>
                  )}
                </div>
                <small className="admin-muted-note">
                  {detail.driver.gitReviewedAt
                    ? `Last reviewed ${formatDateTime(detail.driver.gitReviewedAt)}${detail.driver.gitReviewedBy ? ` by ${detail.driver.gitReviewedBy}` : ''}.`
                    : 'No GIT review decision has been saved yet.'}
                </small>
                {detail.driver.restrictedGoodsAcknowledgedAt ? (
                  <small className="admin-muted-note">
                    Restricted no-GIT flow acknowledged {formatDateTime(detail.driver.restrictedGoodsAcknowledgedAt)}.
                  </small>
                ) : null}
              </div>
            </div>
          </div>

          <div className="admin-section-grid">
            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Identity and verification</h2>
                  <p className="admin-panel-copy">Core account state, verification flags, and base onboarding timestamps.</p>
                </div>
              </div>
              <div className="admin-detail-grid">
                <div className="admin-detail-card"><span>Driver</span><strong>{detail.driver.name}</strong></div>
                <div className="admin-detail-card"><span>Email</span><strong>{detail.driver.email}</strong></div>
                <div className="admin-detail-card"><span>Phone</span><strong>{detail.driver.phoneE164 || 'Not provided'}</strong></div>
                <div className="admin-detail-card"><span>Submitted at</span><strong>{formatDateTime(detail.onboardingSubmittedAt)}</strong></div>
                <div className="admin-detail-card"><span>Email verification</span><strong>{detail.driver.emailVerified ? 'Verified' : 'Pending'}</strong></div>
                <div className="admin-detail-card"><span>Phone verification</span><strong>{detail.driver.phoneVerified ? 'Verified' : 'Pending'}</strong></div>
                <div className="admin-detail-card">
                  <span>Access flags</span>
                  <strong>{detail.driver.accountNonLocked === false ? 'Locked' : 'Unlocked'} · {detail.driver.accountNonExpired === false ? 'Expired' : 'Active'}</strong>
                </div>
                <div className="admin-detail-card">
                  <span>Credential state</span>
                  <strong>{detail.driver.credentialsNonExpired === false ? 'Credentials expired' : 'Credentials valid'}</strong>
                </div>
              </div>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Account controls and matching</h2>
                  <p className="admin-panel-copy">Update account access flags, early-adopter status, and company matching without leaving this review.</p>
                </div>
              </div>
              <div className="admin-detail-grid">
                <div className="admin-detail-card">
                  <span>Account lock</span>
                  <strong>{isLocked ? 'Locked' : 'Unlocked'}</strong>
                  <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="register-secondary-button"
                      disabled={Boolean(actionLoading)}
                      onClick={handleLockToggle}
                      type="button"
                    >
                      {actionLoading === 'lock' ? 'Saving...' : isLocked ? 'Unlock account' : 'Lock account'}
                    </button>
                  </div>
                </div>
                <div className="admin-detail-card">
                  <span>Account enabled</span>
                  <strong>{isEnabled ? 'Enabled' : 'Disabled'}</strong>
                  <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="register-secondary-button"
                      disabled={Boolean(actionLoading)}
                      onClick={handleEnabledToggle}
                      type="button"
                    >
                      {actionLoading === 'enabled' ? 'Saving...' : isEnabled ? 'Disable account' : 'Enable account'}
                    </button>
                  </div>
                </div>
                <div className="admin-detail-card">
                  <span>Early adopter</span>
                  <strong>{isEarlyAdopter ? 'Enabled' : 'Disabled'}</strong>
                  <small>
                    {detail.driver.earlyAdopterEffectiveFrom
                      ? `Effective: ${formatDateTime(detail.driver.earlyAdopterEffectiveFrom)}`
                      : 'No early-adopter effective date recorded.'}
                  </small>
                  <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="register-secondary-button"
                      disabled={Boolean(actionLoading)}
                      onClick={handleEarlyAdopterToggle}
                      type="button"
                    >
                      {actionLoading === 'early-adopter' ? 'Saving...' : isEarlyAdopter ? 'Disable early adopter' : 'Enable early adopter'}
                    </button>
                  </div>
                </div>
                <div className="admin-detail-card">
                  <span>Company matching</span>
                  <strong>{detail.driver.companyDetails?.linkStatus || 'No link status yet'}</strong>
                  <small>{normalizedCompanyId || 'No normalized company ID recorded yet.'}</small>
                  <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="register-secondary-button"
                      disabled={Boolean(actionLoading)}
                      onClick={handleDriverMatchingRerun}
                      type="button"
                    >
                      {actionLoading === 'rerun-driver-match' ? 'Rerunning...' : 'Rerun for driver'}
                    </button>
                    {normalizedCompanyId ? (
                      <button
                        className="register-secondary-button"
                        disabled={Boolean(actionLoading)}
                        onClick={handleCompanyMatchingRerun}
                        type="button"
                      >
                        {actionLoading === 'rerun-company-match' ? 'Rerunning...' : 'Rerun for company'}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Bank and company details</h2>
                  <p className="admin-panel-copy">Payment and company-linked data supplied during onboarding.</p>
                </div>
              </div>
              <div className="admin-detail-grid">
                <div className="admin-detail-card"><span>Account holder</span><strong>{bankAccountHolder || 'Not supplied'}</strong></div>
                <div className="admin-detail-card"><span>Bank</span><strong>{detail.driver.bankDetails?.bankName || 'Not supplied'}</strong></div>
                {bankBranchCode ? (
                  <div className="admin-detail-card"><span>Branch code</span><strong>{bankBranchCode}</strong></div>
                ) : null}
                <div className="admin-detail-card"><span>Bank verification</span><strong>{bankVerificationStatus}</strong></div>
                <div className="admin-detail-card"><span>Verified at</span><strong>{formatDateTime(bankVerifiedAt)}</strong></div>
                {!isIndividualOwner ? (
                  <>
                    <div className="admin-detail-card"><span>Company</span><strong>{detail.driver.companyDetails?.companyName || 'No company details'}</strong></div>
                    <div className="admin-detail-card"><span>Company email</span><strong>{detail.driver.companyDetails?.companyEmail || 'Not supplied'}</strong></div>
                    <div className="admin-detail-card"><span>Link status</span><strong>{detail.driver.companyDetails?.linkStatus || 'Pending'}</strong></div>
                    <div className="admin-detail-card"><span>Normalized company ID</span><strong>{normalizedCompanyId || 'Not generated yet'}</strong></div>
                    <div className="admin-detail-card"><span>Company claimed</span><strong>{detail.driver.companyDetails?.companyClaimed ? 'Claimed' : 'Unclaimed or unknown'}</strong></div>
                    <div className="admin-detail-card"><span>Matching notes</span><strong>{detail.driver.companyDetails?.matchingNotes || 'No matching notes recorded.'}</strong></div>
                  </>
                ) : null}
              </div>
            </section>

            {isIndividualOwner ? (
              <PaystackSubaccountSetup
                copy="Use the verified driver bank account to create a Paystack subaccount in the Paystack Dashboard, then save the resulting split setup here."
                onSave={handleSavePaystackSetup}
                source={detail.driver.bankDetails}
              />
            ) : (
              <>
                {companyPayoutLoading ? (
                  <section className="admin-panel">
                    <div className="register-loader" />
                  </section>
                ) : companyPayoutError ? (
                  <section className="admin-panel">
                    <div className="register-inline-error">{companyPayoutError}</div>
                  </section>
                ) : normalizedCompanyId ? (
                  <PaystackSubaccountSetup
                    copy="Use the linked company payout details to create a Paystack subaccount in the Paystack Dashboard, then save the company split setup here."
                    onSave={handleSavePaystackSetup}
                    source={companyPayoutReview}
                  />
                ) : (
                  <section className="admin-panel">
                    <p className="admin-empty">
                      Link this company driver to a normalized company before saving company Paystack setup.
                    </p>
                  </section>
                )}
              </>
            )}

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Vehicles and attachments</h2>
                  <p className="admin-panel-copy">Open the uploaded documents and confirm supporting materials are complete.</p>
                </div>
              </div>
              <div className="admin-list">
                {(detail.driver.vehicles ?? []).map((vehicle) => (
                  <div className="admin-list-card" key={vehicle.id ?? `${vehicle.make}-${vehicle.registrationNumber}`}>
                    <strong>{vehicle.make || 'Vehicle'} {vehicle.model || ''}</strong>
                    <small>{vehicle.registrationNumber || 'No registration number supplied'}</small>
                    <small>{vehicle.capacityTons ? `${vehicle.capacityTons} tons` : 'Capacity pending'} · {vehicle.status || 'Status pending'}</small>
                  </div>
                ))}
                {!detail.driver.vehicles?.length ? <p className="admin-empty">No vehicles were attached to this onboarding yet.</p> : null}
              </div>
              <div className="admin-list" style={{ marginTop: '14px' }}>
                {attachmentEntries.map(([key, attachment]) => (
                  <div className="admin-list-card" key={key}>
                    <strong>{key}</strong>
                    <small>{attachment.originalFileName || attachment.fileName || 'Uploaded document'}</small>
                    <small>{attachment.contentType || 'Unknown file type'}</small>
                    <div className="admin-inline-actions" style={{ marginTop: '10px' }}>
                      {attachment.url ? (
                        <a className="register-secondary-button" href={attachment.url} rel="noreferrer" target="_blank">
                          Open document
                        </a>
                      ) : (
                        <span className="admin-muted-note">Document URL not available in this environment.</span>
                      )}
                    </div>
                  </div>
                ))}
                {!attachmentEntries.length ? <p className="admin-empty">No uploaded onboarding documents are available yet.</p> : null}
              </div>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Review messaging</h2>
                  <p className="admin-panel-copy">This thread is the onboarding review conversation, separate from trip chat.</p>
                </div>
              </div>
              {conversationError ? <div className="register-inline-error">{conversationError}</div> : null}
              {pendingDriverReplyId ? (
                <div className="admin-message-alert" role="status">
                  <span className="admin-message-alert-dot" />
                  <span>A new driver reply has arrived.</span>
                  <button
                    className="admin-message-alert-button"
                    onClick={() => {
                      syncMessageListPosition();
                      if (latestMessage?.id) {
                        setAcknowledgedLatestMessageId(latestMessage.id);
                      }
                      setPendingDriverReplyId(null);
                    }}
                    type="button"
                  >
                    Jump to latest
                  </button>
                </div>
              ) : null}
              <div
                className="admin-message-list"
                onScroll={handleMessageListScroll}
                ref={messageListRef}
              >
                {messages.map((message) => (
                  <article className={`admin-message-item${message.senderType?.toUpperCase() === 'ADMIN' ? ' admin-message-item-admin' : ''}`} key={message.id}>
                    <div className="admin-message-meta">
                      {message.senderName || message.senderType || 'Message'} · {formatDateTime(message.createdAtIso || message.createdAt)}
                    </div>
                    <p className="admin-message-content">{message.content || 'No text content'}</p>
                    <AdminMessageAttachments attachments={message.attachments} />
                  </article>
                ))}
                {!messages.length ? (
                  <p className="admin-empty">
                    {conversationError
                      ? 'The review detail is available, but this driver conversation could not be opened right now.'
                      : 'No review messages have been sent in this thread yet.'}
                  </p>
                ) : null}
              </div>
              <form className="register-form" onSubmit={handleSendMessage} style={{ marginTop: '14px' }}>
                <label className="register-label" htmlFor="adminReviewMessage">Message to driver</label>
                <textarea
                  className="register-input"
                  id="adminReviewMessage"
                  onChange={(event) => {
                    setMessageDraft(event.target.value);
                    setMessageError('');
                    setMessageSuccess('');
                  }}
                  placeholder="Tell the driver what needs to be corrected or confirmed."
                  rows={5}
                  value={messageDraft}
                />
                {messageError ? <div className="register-inline-error">{messageError}</div> : null}
                {messageSuccess ? <div className="register-inline-success">{messageSuccess}</div> : null}
                <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
                  <button className="register-submit register-submit-inline" disabled={messageLoading} type="submit">
                    {messageLoading ? 'Sending...' : 'Send message'}
                  </button>
                  {conversation ? <span className="admin-muted-note">Conversation: {conversation.conversationId}</span> : null}
                </div>
              </form>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Reject or request corrections</h2>
                  <p className="admin-panel-copy">Add a clear reason so the driver knows exactly what must be fixed.</p>
                </div>
              </div>
              {isApproved ? (
                <p className="admin-empty">
                  Rejection is disabled here because this onboarding is already approved.
                </p>
              ) : (
                <>
                  <div className="register-field">
                    <label className="register-label" htmlFor="rejectReason">Rejection reason</label>
                    <textarea
                      className="register-input"
                      id="rejectReason"
                      onChange={(event) => setRejectReason(event.target.value)}
                      placeholder="Missing PDP document, unreadable ID copy, company details mismatch..."
                      rows={5}
                      value={rejectReason}
                    />
                  </div>
                  <label className="admin-checkbox-row" style={{ marginTop: '14px' }}>
                    <input checked={messageSentSeparately} onChange={(event) => setMessageSentSeparately(event.target.checked)} type="checkbox" />
                    <span>I already sent a separate review message to the driver.</span>
                  </label>
                  <div className="admin-inline-actions" style={{ marginTop: '14px' }}>
                    <button className="register-secondary-button" disabled={Boolean(actionLoading)} onClick={handleReject} type="button">
                      {actionLoading === 'reject' ? 'Rejecting...' : 'Reject onboarding'}
                    </button>
                    {isRejected ? (
                      <span className="admin-muted-note">Onboarding rejected.</span>
                    ) : null}
                  </div>
                </>
              )}
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Change requests and review history</h2>
                  <p className="admin-panel-copy">Review earlier edits, admin actions, and any returned correction cycles.</p>
                </div>
              </div>
              <div className="admin-list">
                {detail.changeRequests.map((request) => (
                  <div className="admin-list-card" key={request.id}>
                    <strong>{request.type || 'Profile change request'}</strong>
                    <small>Status: {request.status || 'Unknown'}</small>
                    <small>Created: {formatDateTime(request.createdAt)}</small>
                    {request.reviewedAt ? <small>Reviewed: {formatDateTime(request.reviewedAt)}</small> : null}
                    {request.reviewedBy ? <small>Reviewed by: {request.reviewedBy}</small> : null}
                    {request.supportDocOriginalName ? <small>Support document: {request.supportDocOriginalName}</small> : null}
                    {request.rejectionReason ? <small>Rejection reason: {request.rejectionReason}</small> : null}
                    <pre className="admin-json-block">{formatReviewDetails(request.payloadJson)}</pre>
                    {request.status?.toUpperCase() === 'PENDING' ? (
                      <div className="register-form" style={{ marginTop: '12px' }}>
                        <div className="admin-inline-actions">
                          <button
                            className="register-submit register-submit-inline"
                            disabled={Boolean(changeRequestLoadingId)}
                            onClick={() => void handleApproveChangeRequest(request.id)}
                            type="button"
                          >
                            {changeRequestLoadingId === request.id ? 'Reviewing...' : 'Approve change'}
                          </button>
                        </div>
                        <label className="register-label" htmlFor={`changeRejectReason-${request.id}`} style={{ marginTop: '12px' }}>
                          Rejection reason
                        </label>
                        <textarea
                          className="register-input"
                          id={`changeRejectReason-${request.id}`}
                          onChange={(event) =>
                            setChangeRequestRejectReasons((current) => ({
                              ...current,
                              [request.id]: event.target.value,
                            }))
                          }
                          placeholder="Explain what the driver must correct before resubmitting this profile change."
                          rows={3}
                          value={changeRequestRejectReasons[request.id] ?? ''}
                        />
                        <div className="admin-inline-actions" style={{ marginTop: '10px' }}>
                          <button
                            className="register-secondary-button"
                            disabled={Boolean(changeRequestLoadingId)}
                            onClick={() => void handleRejectChangeRequest(request.id)}
                            type="button"
                          >
                            {changeRequestLoadingId === request.id ? 'Reviewing...' : 'Reject change'}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ))}
                {!detail.changeRequests.length ? <p className="admin-empty">No change requests were recorded for this driver.</p> : null}
              </div>
              <div className="admin-list" style={{ marginTop: '14px' }}>
                {detail.reviewHistory.map((history, index) => (
                  <div className="admin-list-card" key={`${history.createdAt}-${index}`}>
                    <strong>{history.actionType || 'Review action'}</strong>
                    <small>{history.actorEmail || history.actorUserId || 'System actor'}</small>
                    <small>{formatDateTime(history.createdAt)}</small>
                    <pre className="admin-json-block">{formatReviewDetails(history.detailsJson)}</pre>
                  </div>
                ))}
                {!detail.reviewHistory.length ? <p className="admin-empty">No review history entries were recorded yet.</p> : null}
              </div>
            </section>

            {isSuperAdmin ? (
              <section className="admin-panel">
                <div className="admin-panel-header">
                  <div>
                    <h2 className="admin-panel-title">Driver earnings visibility</h2>
                    <p className="admin-panel-copy">MVP finance support view for commission and payout-related review context.</p>
                  </div>
                </div>
                {earningsError ? <div className="register-inline-error">{earningsError}</div> : null}
                <div className="admin-driver-earnings-list">
                  {earnings.map((row, index) => (
                    <div className="admin-driver-earnings-card" key={`${row.bookingId || 'booking'}-${index}`}>
                      <div className="admin-driver-earnings-main">
                        <span className="admin-driver-earnings-label">Completed</span>
                        <strong>{formatDateTime(row.completedAt)}</strong>
                        <small>{row.bookingId || 'Booking reference pending'}</small>
                      </div>
                      <div className="admin-driver-earnings-money">
                        <span>
                          <small>Gross</small>
                          <strong>{formatCurrency(row.grossAmount)}</strong>
                        </span>
                        <span>
                          <small>Commission</small>
                          <strong>{formatCurrency(row.commissionAmount)}</strong>
                        </span>
                        <span>
                          <small>Net</small>
                          <strong>{formatCurrency(row.netAmount)}</strong>
                        </span>
                      </div>
                      <div className="admin-driver-earnings-plan">
                        <span className={row.earlyAdopter ? 'admin-pill admin-pill-success' : 'admin-pill'}>
                          {row.earlyAdopter ? 'Early adopter' : 'Standard payout'}
                        </span>
                        <strong>{row.commissionPlanCode || 'Default plan'}</strong>
                        <small>{row.commissionRateBps ? `${row.commissionRateBps} bps` : 'Rate not supplied'}</small>
                      </div>
                    </div>
                  ))}
                  {!earnings.length ? <p className="admin-empty">No completed earnings records are available for this driver yet.</p> : null}
                </div>
              </section>
            ) : null}
          </div>
        </>
      ) : (
        <div className="admin-panel">
          <p className="admin-empty">The driver review detail is not available.</p>
        </div>
      )}
    </AdminShell>
  );
};

export default AdminDriverReviewPage;
